/**
 * GTM brand resolution for quote lines (branding plan B3).
 *
 * Per the May 2026 Brand Guidelines, small-scale product identity is
 * expressed through GTM COLOR CODING ("Cortex is always green", p84;
 * GTM main colors pp 50-51) — never through undersized logo marks
 * (minimum digital sizes p14/25). These helpers resolve a SKU to its
 * GTM via brand_sku_rules (served by GET /api/v1/branding) and map the
 * GTM to its --brand-* CSS variable (emitted from brand-tokens.json).
 *
 * Mirrors apps/backend/services/branding.js brandForSku — keep in step.
 */
const rxCache = new Map();
function likeToRegex(pattern) {
    let rx = rxCache.get(pattern);
    if (!rx) {
        rx = new RegExp('^' + pattern.toUpperCase()
            .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            .replace(/%/g, '.*')
            .replace(/_/g, '.') + '$');
        rxCache.set(pattern, rx);
    }
    return rx;
}
/** Resolve a SKU to its GTM brand key ('panw' when nothing matches). */
export function brandForSku(sku, rules) {
    if (!sku || !rules?.length)
        return 'panw';
    const hay = String(sku).toUpperCase();
    const sorted = [...rules].sort((a, b) => a.priority - b.priority);
    for (const r of sorted) {
        if (likeToRegex(r.pattern).test(hay))
            return r.brand;
    }
    return 'panw';
}
export const GTM_LABELS = {
    panw: 'Palo Alto Networks',
    strata: 'Strata',
    prisma: 'Prisma',
    cortex: 'Cortex',
    'cortex-xsiam': 'Cortex XSIAM',
    'cortex-xdr': 'Cortex XDR',
    'cortex-xpanse': 'Cortex Xpanse',
    unit42: 'Unit 42',
    idira: 'Idira',
};
/**
 * The GTM's main-color CSS variable. Cortex sub-brands (XSIAM/XDR/
 * Xpanse) ride the Cortex Intelligence Green family — the guidelines
 * color-code at the GTM level.
 */
export function gtmColorVar(brand) {
    const base = brand.startsWith('cortex') ? 'cortex' : brand;
    return `var(--brand-${base}, var(--brand-panw, #FA582D))`;
}
/**
 * Browse-tree category → iconography-library path (B3b). Keyword match
 * on the ETC workbook's product names; every path verified against the
 * extracted manifest. Returns null when nothing fits — a chip without a
 * glyph is better than a wrong glyph.
 */
const CATEGORY_GLYPHS = [
    [/iot/i, 'network/iot-i.svg'],
    [/sd-wan/i, 'products-and-services/sd-wan-products.svg'],
    [/dlp/i, 'products-and-services/dlp.svg'],
    [/aiops/i, 'products-and-services/aiops-for-ngfw-products.svg'],
    [/saas/i, 'products-and-services/saas-products.svg'],
    [/threat prevention/i, 'products-and-services/advanced-threat-prevention-products.svg'],
    [/wildfire/i, 'products-and-services/wildfire.svg'],
    [/url filtering/i, 'products-and-services/url-filtering-products.svg'],
    [/dns security/i, 'endpoint-threats-exploits-and-malware/inline-machine-learning-dns-security.svg'],
    [/globalprotect/i, 'products-and-services/globalprotect-products.svg'],
    [/prisma access/i, 'products-and-services/prisma-access-agent.svg'],
    [/professional services/i, 'industry/professional-services.svg'],
    [/support/i, 'people/technical-services-support.svg'],
    [/nfr|lab/i, 'general/lab-glassware.svg'],
    [/platform|firewall/i, 'network/firewall.svg'],
    [/subscription|bundle/i, 'general/certificate.svg'],
];
export function categoryGlyphPath(category) {
    if (!category)
        return null;
    for (const [rx, path] of CATEGORY_GLYPHS) {
        if (rx.test(category))
            return path;
    }
    return null;
}
/**
 * Infer a group's identity from the lines under it (B3c): the header
 * icon is always derived live from the members, and the title is the
 * DEFAULT a blank-titled group auto-names to (editable — an explicit
 * title always wins). Anchored on the most significant product first:
 * a group holding a PA-5410 plus its subs, optics, and support is
 * "Firewall 5410", not "Optics".
 *
 * Glyph paths reuse only manifest-verified entries (CATEGORY_GLYPHS /
 * Omnibox) — never invent a path here without checking the library.
 */
export function inferGroupMeta(members) {
    const real = (members ?? []).filter((m) => (m.sku && m.sku !== 'CUSTOM') || m.description);
    if (!real.length)
        return { title: null, glyph: null };
    const all = real
        .map((m) => `${m.sku ?? ''} ${m.description ?? ''}`.toUpperCase())
        .join(' \n ');
    // 1. Hardware firewall family — PAN-PA-<model> anywhere in the group
    // anchors it (subscriptions/support SKUs carry the model too).
    const models = new Set();
    for (const m of real) {
        const hit = String(m.sku ?? '').toUpperCase().match(/^PAN-PA-(\d{3,5})/);
        if (hit)
            models.add(hit[1]);
    }
    if (models.size) {
        const sorted = [...models].sort();
        return {
            title: sorted.length === 1 ? `Firewall ${sorted[0]}` : `Firewalls ${sorted.join(' · ')}`,
            glyph: 'network/firewall.svg',
        };
    }
    if (real.some((m) => /^PAN-VM-/.test(String(m.sku ?? '').toUpperCase())) || /VM-SERIES/.test(all)) {
        return { title: 'VM-Series Firewall', glyph: 'network/firewall.svg' };
    }
    // 2. Named product families, most specific first.
    if (/PRISMA ACCESS|PAN-PRA-/.test(all))
        return { title: 'Prisma Access', glyph: 'products-and-services/prisma-access-agent.svg' };
    if (/GLOBALPROTECT/.test(all))
        return { title: 'GlobalProtect', glyph: 'products-and-services/globalprotect-products.svg' };
    if (/XSIAM/.test(all))
        return { title: 'Cortex XSIAM', glyph: null };
    if (/XPANSE/.test(all))
        return { title: 'Cortex Xpanse', glyph: null };
    if (/\bXDR\b/.test(all))
        return { title: 'Cortex XDR', glyph: null };
    if (/CORTEX/.test(all))
        return { title: 'Cortex', glyph: null };
    if (/SD-?WAN/.test(all))
        return { title: 'SD-WAN', glyph: 'products-and-services/sd-wan-products.svg' };
    if (/PANORAMA/.test(all))
        return { title: 'Panorama Management', glyph: 'products-and-services/aiops-for-ngfw-products.svg' };
    // 3. Homogeneous utility groups.
    const every = (rx) => real.every((m) => rx.test(`${m.sku ?? ''} ${m.description ?? ''}`.toUpperCase()));
    if (every(/PAN-SVC|SUPPORT/))
        return { title: 'Support & Services', glyph: 'people/technical-services-support.svg' };
    if (every(/QSFP|SFP|-DAC|TRANSCEIVER|OPTIC|CABLE/))
        return { title: 'Optics & Cabling', glyph: null };
    // 4. Dominant product family from the catalog snapshot.
    const famCount = new Map();
    for (const m of real) {
        const f = (m.productFamily ?? '').trim();
        if (f)
            famCount.set(f, (famCount.get(f) ?? 0) + 1);
    }
    const top = [...famCount.entries()].sort((a, b) => b[1] - a[1])[0];
    if (top)
        return { title: top[0], glyph: categoryGlyphPath(top[0]) };
    return { title: null, glyph: null };
}
